import { useEffect, useState } from 'react'

import { LogErrors } from './../../../utilities/LogErrors'

import { ValidateNumberOfExamsTaken } from './../../../utilities/BL/Student/Courses'

/**
 * ============================================================
 * Item
 * ============================================================
 * Renderiza un elemento (Item / Activity / Exam) dentro del listado de Topics.
 *
 * Reglas IMPORTANTES (ya existentes):
 * 1) Si item.enabled === false => siempre se muestra bloqueado con candado.
 * 2) Si item.type === 1 (Examen):
 *    - Si es "Evaluación diagnóstica" se valida allowTakeExam con ValidateNumberOfExamsTaken.
 *    - Si allowTakeExam === false => se muestra bloqueado con candado.
 *
 * NUEVA regla (agregada):
 * 3) Si item.type === 1 y tiene agenda:
 *    - Si item.agenda.isActiveNow indica que NO está en horario/fecha
 *      => se muestra bloqueado con candado + fechas/horas.
 *
 * Nota crítica:
 * - isActiveNow a veces NO llega como boolean real.
 *   Puede llegar como "true"/"false", 1/0, "1"/"0".
 *   Por eso se normaliza antes de evaluar.
 */
const Item = ({ item, topicRefs, goToInstrument, isApproved, isViewed }) => {
  const [allowTakeExam, setAllowTakeExam] = useState(true)

  /**
   * ============================================================
   * LoadPage (lógica existente)
   * ============================================================
   * Únicamente aplica a "Evaluación diagnóstica" (type=1).
   * Se consulta si el alumno todavía puede presentar ese examen.
   */
  const LoadPage = async (item) => {
    try {
      // Solo aplica para Evaluación diagnóstica
      if (item.type === 1 && item.name.toLowerCase() === 'evaluación diagnóstica'.toLowerCase()) {
        const responseTakeExam = await ValidateNumberOfExamsTaken(item)

        if (responseTakeExam.success) {
          const allow = responseTakeExam?.data?.allowTakeExam

          // Si el backend regresa un valor definido, lo usamos
          if (allow !== undefined && allow !== null) setAllowTakeExam(allow)
        }
      }
    } catch (error) {
      await LogErrors('GoToInstrument - Blocks', error)
    }
  }

  useEffect(() => {
    LoadPage(item)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * ============================================================
   * Helpers para icono de aprobación (type 2)
   * ============================================================
   * - Si isApproved es null => no se muestra nada
   * - Si true => icono verde
   * - Si false => icono deshabilitado
   */
  const approvedIconClass =
    isApproved === null
      ? ''
      : isApproved === true
        ? 'bi bi-check-circle-fill Topics-Item__Icon--Success'
        : 'bi bi-check-circle-fill Topics-Item__Icon--Disabled'

  // const approvedLockPrefix = isApproved ? <i className="bi bi-check-circle-fill Topics-Item__Icon--Success"></i> : null

  /**
   * ============================================================
   * 1) Item bloqueado por enabled=false (LÓGICA EXISTENTE)
   * ============================================================
   * Este return se mantiene INTACTO.
   */
  if (item.enabled === false) {
    return (
      <li
        className={`Topics-${item.type === 1 ? 'Exam' : item.type === 2 ? 'Item' : 'Activity'}`}
        ref={(el) => (topicRefs.current[item.id] = el)}
      >
        <span><i className="bi bi-lock"></i> {item.name}</span>

        {item.type === 1 && item.agenda ? (
            <span className='Topics-Exam__DateTime'>
                <span>Del {item.agenda.startDate} al {item.agenda.finishDate}</span>
                <span>Entre {item.agenda.startHour} hrs. y {item.agenda.finishHour} hrs.</span>
            </span>
        ) : item.type === 1 && (
            <span className='Topics-Exam__DateTime'>
                <span>Esta evaluación no ha sido agendada por tu instructor</span>
            </span>
        )}
      </li>
    )
  }

  /**
   * ============================================================
   * 2) EXAMEN (type === 1)
   * ============================================================
   * - NUEVA validación: si hay agenda y NO está activa ahora => bloqueado
   * - Lógica existente: allowTakeExam para diagnóstica
   */
  if (item.type === 1) {
    /**
     * ------------------------------------------------------------
     * 2.1) NUEVA VALIDACIÓN: fuera de horario/fecha (agenda)
     * ------------------------------------------------------------
     * Solo aplica cuando existe agenda.
     *
     * Importante: normalizamos isActiveNow porque puede llegar como:
     *  - boolean: true/false
     *  - string: "true"/"false"
     *  - number: 1/0
     *  - string numérica: "1"/"0"
     */
    const hasAgenda = !!item.agenda
    const rawIsActiveNow = item.agenda?.isActiveNow

    // Normalización a boolean "real"
    const isActiveNow =
      rawIsActiveNow === true ||
      rawIsActiveNow === 'true' ||
      rawIsActiveNow === 1 ||
      rawIsActiveNow === '1'

    /**
     * shouldLockBySchedule:
     * - Si hay agenda y NO está activa => bloquear con candado
     * - Si NO hay agenda, NO se bloquea por esta regla (se respeta comportamiento actual)
     */
    const shouldLockBySchedule = hasAgenda && !isActiveNow

    if (shouldLockBySchedule) {
      return (
        <li className='Topics-Exam Topics-Exam--Inactive' ref={(el) => (topicRefs.current[item.id] = el)}>
          <span><i className="bi bi-lock"></i> {item.name}</span>

          <span className='Topics-Exam__DateTime'>
            <span>Del {item.agenda.startDate} al {item.agenda.finishDate}</span>
            <span>Entre {item.agenda.startHour} hrs. y {item.agenda.finishHour} hrs.</span>
          </span>
        </li>
      )
    }

    /**
     * ------------------------------------------------------------
     * 2.2) LÓGICA EXISTENTE: allowTakeExam (diagnóstica)
     * ------------------------------------------------------------
     * Si allowTakeExam === true => clickable
     * Si allowTakeExam === false => bloqueado con candado
     */
    if (allowTakeExam) {
      return (
        <li className='Topics-Exam' onClick={() => goToInstrument(item)} ref={(el) => (topicRefs.current[item.id] = el)}>
          <span>{item.name}</span>

          {item.agenda && (
            <span className='Topics-Exam__DateTime'>
              <span>Del {item.agenda.startDate} al {item.agenda.finishDate}</span>
              <span>Entre {item.agenda.startHour} hrs. y {item.agenda.finishHour} hrs.</span>
            </span>
          )}
        </li>
      )
    }

    // allowTakeExam === false => bloqueado (lógica existente)
    return (
      <li className='Topics-Exam' ref={(el) => (topicRefs.current[item.id] = el)}>
        <span><i className="bi bi-lock"></i> {item.name}</span>

        {item.agenda && (
          <span className='Topics-Exam__DateTime'>
            <span>Del {item.agenda.startDate} al {item.agenda.finishDate}</span>
            <span>Entre {item.agenda.startHour} hrs. y {item.agenda.finishHour} hrs.</span>
          </span>
        )}
      </li>
    )
  }

  /**
   * ============================================================
   * 3) Tema de contenido (type 2) o actividad (type 3)
   * ============================================================
   * Lógica existente, SIN cambios.
   */
  return (
    item.type === 3 ? (
      <li className='Topics-Activity' onClick={() => goToInstrument(item)} ref={(el) => (topicRefs.current[item.id] = el)}>{item.name}</li>
    ) : (
      <li className='Topics-Item' onClick={() => goToInstrument(item)} ref={(el) => (topicRefs.current[item.id] = el)}>
        {/* {approvedLockPrefix} */}
        {item.name}
        <i className={`${approvedIconClass}`}></i>
      </li>
    )
  )
}

export { Item };